import { memo } from "react"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { styled, Theme } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import { useAppSelector } from "appRedux/hooks"
import { updateWishListInfo } from "appRedux/slices/wishListSlice"
import { PrivacyOption } from "constants/options"
import { OPEN_LIST_ACTIONS_BOTTOM_SHEET } from "constants/events"
import { getPublicAwarePath, getStaticAssetUrl } from "appRedux/baseQuery"
import MoreIcon from "components/atoms/_icons/MoreIcon"
import LazyImage from "components/atoms/LazyImage"

type WishListSummaryCardProps = {
  listInfo: WishList;
  privacyOptionDetail: PrivacyOption | undefined;
  onMoreButtonClicked: (list: WishList) => void;
}

export default memo(function WishListSummaryCard(props: WishListSummaryCardProps) {
  const { listInfo, privacyOptionDetail, onMoreButtonClicked } = props
  const { id, name, listItems } = listInfo

  const currentPageInfo = useAppSelector((state) => state.global.currentPageInfo)

  const navigate = useNavigate()
  const dispatch = useDispatch()

  function navigateToListPage() {
    dispatch(updateWishListInfo(listInfo))
    const handle = currentPageInfo?.dynamicPathParts?.handle
    navigate(getPublicAwarePath(`/user/${handle}/list/${id}`))
  }

  function handleOpenMoreActionsBottomSheet() {
    document.dispatchEvent(new CustomEvent(OPEN_LIST_ACTIONS_BOTTOM_SHEET))
    onMoreButtonClicked(listInfo)
  }

  return (
    <Container>
      {listItems ? <Thumbnail
        isempty={0}
        onClick={navigateToListPage}
      >
        <Stack spacing={2} direction="column" width={"100%"}>
          {listItems.slice(0, 2).map((listItem: WishItem) => (
            renderItemImage(listItem)
          ))}
        </Stack>
        {listItems.length > 2 ? <Stack spacing={2} direction="column" width={"100%"}>
          {listItems.slice(2, 4).map((listItem: WishItem) => (
            renderItemImage(listItem)
          ))}
        </Stack> : null}
      </Thumbnail> : <Thumbnail
        isempty={1}
        onClick={navigateToListPage}
      >
      </Thumbnail>}
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={6}>
        <Stack direction="row" alignItems="center" gap={2} minWidth={0}>
          <PrivacyIconWrapper>
            {privacyOptionDetail?.filledIcon}
          </PrivacyIconWrapper>
          <ListName>{name}</ListName>
        </Stack>
        <MoreIconWrapper onClick={handleOpenMoreActionsBottomSheet}>
          <MoreIcon/>
        </MoreIconWrapper>
      </Stack>
    </Container>
  )
})

function renderItemImage(listItem: WishItem) {
  return <ImageContainer key={listItem.id}>
    <LazyImage
      src={typeof listItem.image === "string" ? getStaticAssetUrl(listItem.image) : null}
      alt={listItem.name}
    />
  </ImageContainer>
}

const Container = styled(Stack)(({ theme }) => ({
  gap: "8px",

  "& svg": {
    width: 20,
    height: 20
  }
}))

type ThumbnailProps = {
  theme?: Theme;
  isempty: 1 | 0;
}

const Thumbnail = styled(Stack)(({ theme, isempty }: ThumbnailProps) => ({
  width: "100%",
  height: "100%",
  flexDirection: "row-reverse",
  aspectRatio: 1,
  border: "1px solid",
  borderColor: isempty ? theme?.palette.gray["7"] : "transparent",
  backgroundColor: isempty ? theme?.palette.gray["4"] : "transparent",
  borderRadius: "8px",
  gap: "4px"
}))

const ListName = styled(Stack)(({ theme }) => ({
  justifyContent: "center",
  fontSize: "1.2rem",
  color: theme.palette.gray["12"],
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  paddingTop: "2px"
}))

const PrivacyIconWrapper = styled(Stack)(({ theme }) => ({
  flexShrink: 0,
  width: 24,
  height: 24,
  justifyContent: "center",
  alignItems: "center"
}))

const ImageContainer = styled(Stack)(({ theme }) => ({
  borderRadius: "8px",
  backgroundColor: theme.palette.gray["4"],
  border: `1px solid ${theme.palette.gray["6"]}`,
  position: "relative",
  height: "100%",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden"
}))

const MoreIconWrapper = styled(Stack)(({ theme }) => ({
  width: "22px",
  height: "22px",
  justifyContent: "center",
  alignItems: "center",

  "& svg": {
    stroke: theme.palette.gray["12"],
    width: 24,
    height: 24
  }
}))
